import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import axios from 'axios';
import { toast } from 'sonner';
import { Settings, ChevronDown, Sparkles, FileText, RefreshCw } from 'lucide-react';
import ParagraphModule from '@/components/ParagraphModule';
import DirectTextModule from '@/components/DirectTextModule';
import ParaphraseModule from '@/components/ParaphraseModule';
import CitationsBox from '@/components/CitationsBox';
import CustomizationSidebar from '@/components/CustomizationSidebar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GenerationPage() {
  const [sessionId] = useState(() => `session-${Date.now()}`);
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState({ provider: 'openai', name: 'gpt-4' });
  const [tokensUsed, setTokensUsed] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  
  // Mode selection: 'ai-generation', 'direct-text', 'paraphrase'
  const [mode, setMode] = useState('ai-generation');
  
  // Paragraphs state
  const [paragraphs, setParagraphs] = useState([]);
  const [confirmedCount, setConfirmedCount] = useState(0);
  const [loadedModules, setLoadedModules] = useState(1); // Start with 1 module visible
  
  // Citations state
  const [citations, setCitations] = useState([]);
  
  // Customization settings
  const [customSettings, setCustomSettings] = useState({
    title: '',
    author: '',
    affiliation: '',
    citationStyle: 'APA',
    tone: 'academic',
    readabilityTarget: '',
    wordCountTarget: '',
    excludedWords: '' // New field for words to exclude
  });
  
  // Session management
  const [savedSessions, setSavedSessions] = useState([]);
  const [showSessionsModal, setShowSessionsModal] = useState(false);

  // Load saved sessions from localStorage on mount
  useEffect(() => {
    const sessions = localStorage.getItem('publication-builder-sessions');
    if (sessions) {
      try {
        setSavedSessions(JSON.parse(sessions));
      } catch (error) {
        console.error('Error loading sessions:', error);
      }
    }
  }, []);

  // Load saved data from localStorage on mount
  useEffect(() => {
    const savedData = localStorage.getItem('publication-builder-draft');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        if (parsed.paragraphs) setParagraphs(parsed.paragraphs);
        if (parsed.confirmedCount) setConfirmedCount(parsed.confirmedCount);
        if (parsed.loadedModules) setLoadedModules(parsed.loadedModules);
        if (parsed.customSettings) setCustomSettings(parsed.customSettings);
        if (parsed.tokensUsed) setTokensUsed(parsed.tokensUsed);
        if (parsed.mode) setMode(parsed.mode);
        toast.success('Draft restored from previous session');
      } catch (error) {
        console.error('Error loading draft:', error);
      }
    }
  }, []);

  // Auto-save to localStorage whenever data changes
  useEffect(() => {
    const dataToSave = {
      paragraphs,
      confirmedCount,
      loadedModules,
      customSettings,
      tokensUsed,
      mode,
      lastSaved: new Date().toISOString()
    };
    localStorage.setItem('publication-builder-draft', JSON.stringify(dataToSave));
  }, [paragraphs, confirmedCount, loadedModules, customSettings, tokensUsed, mode]);

  // Load available models on mount
  useEffect(() => {
    fetchAvailableModels();
    fetchCitations();
  }, []);

  const fetchAvailableModels = async () => {
    try {
      const response = await axios.get(`${API}/admin/generation/models`, {
        withCredentials: true
      });
      setAvailableModels(response.data.models);
    } catch (error) {
      console.error('Error fetching models:', error);
      toast.error('Failed to load AI models');
    }
  };

  const fetchCitations = async () => {
    try {
      const response = await axios.get(`${API}/admin/generation/citations`, {
        withCredentials: true
      });
      setCitations(response.data.citations);
    } catch (error) {
      console.error('Error fetching citations:', error);
    }
  };

  const handleParagraphConfirm = (paragraphData) => {
    setParagraphs(prev => {
      const existing = prev.findIndex(p => p.order === paragraphData.order);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = paragraphData;
        return updated;
      }
      return [...prev, paragraphData].sort((a, b) => a.order - b.order);
    });
    
    setConfirmedCount(prev => prev + 1);
    
    // Reveal next module if under 20
    if (loadedModules < 20) {
      setLoadedModules(prev => prev + 1);
    }
  };

  const handleTokensUsed = (tokens) => {
    setTokensUsed(prev => prev + tokens);
  };
  
  const handleSaveSession = () => {
    if (!customSettings.title) {
      toast.error('Please set a title before saving');
      return;
    }
    
    const sessionData = {
      id: `session-${Date.now()}`,
      title: customSettings.title,
      paragraphs,
      confirmedCount,
      loadedModules,
      customSettings,
      tokensUsed,
      mode,
      savedAt: new Date().toISOString()
    };
    
    const currentSessions = [...savedSessions];
    
    // Keep only the 5 most recent sessions
    if (currentSessions.length >= 5) {
      currentSessions.shift(); // Remove oldest
    }
    
    currentSessions.push(sessionData);
    setSavedSessions(currentSessions);
    localStorage.setItem('publication-builder-sessions', JSON.stringify(currentSessions));
    toast.success(`Session "${customSettings.title}" saved!`);
  };
  
  const handleLoadSession = (session) => {
    setParagraphs(session.paragraphs);
    setConfirmedCount(session.confirmedCount);
    setLoadedModules(session.loadedModules);
    setCustomSettings(session.customSettings);
    setTokensUsed(session.tokensUsed);
    if (session.mode) setMode(session.mode);
    setShowSessionsModal(false);
    toast.success(`Loaded session: ${session.title}`);
  };
  
  const handleDeleteSession = (sessionId) => {
    const updated = savedSessions.filter(s => s.id !== sessionId);
    setSavedSessions(updated);
    localStorage.setItem('publication-builder-sessions', JSON.stringify(updated));
    toast.success('Session deleted');
  };

  const handleCreateChapter = async () => {
    if (paragraphs.length === 0) {
      toast.error('Please create at least one paragraph');
      return;
    }
    
    if (!customSettings.title) {
      toast.error('Please set a title in the customization panel');
      return;
    }
    
    try {
      // First, save all paragraphs that don't have real IDs yet
      const savedParagraphIds = [];
      
      for (const para of paragraphs) {
        // Check if it's a temporary ID
        if (para.id.startsWith('temp-')) {
          // Save this paragraph
          const saveResponse = await axios.post(
            `${API}/admin/generation/paragraphs`,
            {
              session_id: sessionId,
              content: para.content,
              order: para.order,
              main_idea: para.main_idea || null,
              paragraph_id: null
            },
            { withCredentials: true }
          );
          savedParagraphIds.push(saveResponse.data.id);
        } else {
          // Already saved, use existing ID
          savedParagraphIds.push(para.id);
        }
      }
      
      // Now create the chapter with the saved paragraph IDs
      const response = await axios.post(
        `${API}/admin/generation/chapters`,
        {
          title: customSettings.title,
          paragraph_ids: savedParagraphIds,
          order: 1
        },
        { withCredentials: true }
      );
      
      toast.success('Chapter created successfully!');
      
      // Clear the draft from localStorage
      localStorage.removeItem('publication-builder-draft');
      
      // Navigate to chapter assembly page
      window.location.href = `/admin/chapter-assembly?chapter_id=${response.data.id}`;
    } catch (error) {
      console.error('Error creating chapter:', error);
      toast.error('Failed to create chapter');
    }
  };

  const handleClearDraft = () => {
    if (window.confirm('Are you sure you want to clear your draft? This will delete all unsaved paragraphs.')) {
      localStorage.removeItem('publication-builder-draft');
      setParagraphs([]);
      setConfirmedCount(0);
      setLoadedModules(1);
      setTokensUsed(0);
      setMode('ai-generation');
      toast.success('Draft cleared');
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-[#F4E4C1] to-[#E8D5B7]">
        {/* Sticky Header */}
        <div className="sticky top-0 z-50 bg-[#6B4423] text-[#F4E4C1] shadow-lg">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between mb-3">
              <h1 className="text-3xl font-bold retro-title">Generation</h1>
              
              <div className="flex items-center gap-4">
                {/* Model Selector - Only show for AI-powered modes */}
                {(mode === 'ai-generation' || mode === 'paraphrase') && (
                  <div className="relative">
                    <select
                      value={`${selectedModel.provider}/${selectedModel.name}`}
                      onChange={(e) => {
                        const [provider, name] = e.target.value.split('/');
                        setSelectedModel({ provider, name });
                      }}
                      className="bg-[#F4E4C1] text-black px-4 py-2 pr-8 rounded border-2 border-[#8B6F47] appearance-none cursor-pointer font-semibold"
                    >
                      {availableModels.map(model => (
                        <option key={`${model.provider}/${model.name}`} value={`${model.provider}/${model.name}`}>
                          {model.display_name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 pointer-events-none" />
                  </div>
                )}
                
                {/* Token Meter */}
                <div className="bg-[#8B6F47] px-4 py-2 rounded border-2 border-[#F4E4C1]">
                  <span className="text-sm">Tokens: {tokensUsed.toLocaleString()}</span>
                </div>
                
                {/* Save Session Button */}
                <button
                  onClick={handleSaveSession}
                  className="retro-button-small bg-[#4D9E7C] hover:bg-[#3D8E6C]"
                  title="Save current session"
                >
                  Save Session
                </button>
                
                {/* Load Sessions Button */}
                <button
                  onClick={() => setShowSessionsModal(true)}
                  className="retro-button-small bg-[#E8934D] hover:bg-[#D8834D]"
                  title="Load saved sessions"
                >
                  Sessions ({savedSessions.length}/5)
                </button>
                
                {/* Settings Button */}
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="retro-button flex items-center gap-2"
                >
                  <Settings className="w-5 h-5" />
                  Settings
                </button>
              </div>
            </div>
            
            {/* Mode Selection */}
            <div className="border-t border-[#F4E4C1]/30 pt-3 mb-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[#F4E4C1] font-bold text-sm">Creation Mode:</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setMode('ai-generation')}
                  className={`px-4 py-2 rounded-lg font-bold text-sm transition-all flex items-center gap-2 ${
                    mode === 'ai-generation'
                      ? 'bg-[#F4E4C1] text-[#6B4423] shadow-lg scale-105'
                      : 'bg-[#8B6F47] text-[#F4E4C1] hover:bg-[#9B7F57]'
                  }`}
                >
                  <Sparkles className="w-4 h-4" />
                  AI Generation
                </button>
                <button
                  onClick={() => setMode('direct-text')}
                  className={`px-4 py-2 rounded-lg font-bold text-sm transition-all flex items-center gap-2 ${
                    mode === 'direct-text'
                      ? 'bg-[#F4E4C1] text-[#6B4423] shadow-lg scale-105'
                      : 'bg-[#8B6F47] text-[#F4E4C1] hover:bg-[#9B7F57]'
                  }`}
                >
                  <FileText className="w-4 h-4" />
                  Direct Text
                </button>
                <button
                  onClick={() => setMode('paraphrase')}
                  className={`px-4 py-2 rounded-lg font-bold text-sm transition-all flex items-center gap-2 ${
                    mode === 'paraphrase'
                      ? 'bg-[#F4E4C1] text-[#6B4423] shadow-lg scale-105'
                      : 'bg-[#8B6F47] text-[#F4E4C1] hover:bg-[#9B7F57]'
                  }`}
                >
                  <RefreshCw className="w-4 h-4" />
                  AI Paraphrase
                </button>
              </div>
            </div>
            
            {/* Navigation Buttons */}
            <div className="flex gap-2 border-t border-[#F4E4C1]/30 pt-3">
              <button
                onClick={() => window.location.href = '/admin'}
                className="retro-button text-sm"
              >
                Admin Dashboard
              </button>
              <button
                onClick={() => window.location.href = '/admin/publication-assembly'}
                className="retro-button text-sm"
              >
                Publication Assembly
              </button>
              <button
                onClick={() => window.location.href = '/admin/publication-vault'}
                className="retro-button text-sm"
              >
                Publication Vault
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex gap-6">
            {/* Main Content Area */}
            <div className="flex-1">
              <div className="space-y-6">
                {/* Mode Description */}
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border-2 border-[#F4E4C1]">
                  {mode === 'ai-generation' && (
                    <p className="text-[#F4E4C1]">
                      <strong>AI Generation Mode:</strong> Provide prompts and main ideas. AI will generate professional content from scratch.
                    </p>
                  )}
                  {mode === 'direct-text' && (
                    <p className="text-[#F4E4C1]">
                      <strong>Direct Text Mode:</strong> Write or paste your own content directly. AI will only format it in the final publication.
                    </p>
                  )}
                  {mode === 'paraphrase' && (
                    <p className="text-[#F4E4C1]">
                      <strong>AI Paraphrase Mode:</strong> Paste existing text. AI will rewrite it to avoid plagiarism while preserving meaning.
                    </p>
                  )}
                </div>
                
                {/* Paragraph Modules - Render based on mode */}
                {[...Array(loadedModules)].map((_, index) => {
                  if (mode === 'ai-generation') {
                    return (
                      <ParagraphModule
                        key={`ai-${index}`}
                        order={index + 1}
                        sessionId={sessionId}
                        selectedModel={selectedModel}
                        onConfirm={handleParagraphConfirm}
                        onTokensUsed={handleTokensUsed}
                        previousParagraphs={paragraphs.filter(p => p.order < index + 1)}
                        maxParagraphs={20}
                        customSettings={customSettings}
                        initialParagraph={paragraphs.find(p => p.order === index + 1)}
                      />
                    );
                  } else if (mode === 'direct-text') {
                    return (
                      <DirectTextModule
                        key={`direct-${index}`}
                        order={index + 1}
                        sessionId={sessionId}
                        onConfirm={handleParagraphConfirm}
                        previousParagraphs={paragraphs.filter(p => p.order < index + 1)}
                        maxParagraphs={20}
                        customSettings={customSettings}
                        initialParagraph={paragraphs.find(p => p.order === index + 1)}
                      />
                    );
                  } else if (mode === 'paraphrase') {
                    return (
                      <ParaphraseModule
                        key={`paraphrase-${index}`}
                        order={index + 1}
                        sessionId={sessionId}
                        selectedModel={selectedModel}
                        onConfirm={handleParagraphConfirm}
                        onTokensUsed={handleTokensUsed}
                        previousParagraphs={paragraphs.filter(p => p.order < index + 1)}
                        maxParagraphs={20}
                        customSettings={customSettings}
                        initialParagraph={paragraphs.find(p => p.order === index + 1)}
                      />
                    );
                  }
                  return null;
                })}
                
                {/* Paragraphs Remaining */}
                <div className="text-center text-white font-semibold text-lg">
                  Paragraph {confirmedCount} of up to 20 confirmed
                  {loadedModules < 20 && ` • ${20 - loadedModules} modules remaining`}
                </div>

                {/* Create Chapter Button */}
                {confirmedCount > 0 && (
                  <div className="flex justify-center gap-4 mt-8">
                    <button
                      onClick={handleClearDraft}
                      className="retro-button bg-gray-600 hover:bg-gray-700 text-lg px-6 py-3"
                    >
                      Clear Draft
                    </button>
                    <button
                      onClick={handleCreateChapter}
                      className="retro-button text-lg px-8 py-3"
                    >
                      Create Chapter ({confirmedCount} paragraph{confirmedCount !== 1 ? 's' : ''})
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Right Sidebar - Customization */}
            <CustomizationSidebar
              settings={customSettings}
              onChange={setCustomSettings}
            />
          </div>

          {/* Citations Box 21 - Always Visible */}
          <CitationsBox
            citations={citations}
            onRefresh={fetchCitations}
            citationStyle={customSettings.citationStyle}
          />
        </div>
        
        {/* Sessions Modal */}
        {showSessionsModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-[#F4E4C1] rounded-lg shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-y-auto">
              <div className="sticky top-0 bg-[#6B4423] text-white p-6 flex justify-between items-center">
                <h2 className="text-2xl font-bold">Saved Sessions ({savedSessions.length}/5)</h2>
                <button
                  onClick={() => setShowSessionsModal(false)}
                  className="text-white hover:text-[#E85D4D] text-2xl"
                >
                  ×
                </button>
              </div>
              
              <div className="p-6">
                {savedSessions.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-[#6B4423] text-lg mb-4">No saved sessions yet</p>
                    <p className="text-[#8B6F47]">Click "Save Session" to save your current work</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {savedSessions.map((session) => (
                      <div
                        key={session.id}
                        className="bg-white border-2 border-[#8B6F47] rounded-lg p-4 hover:border-[#6B4423] transition-colors"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <h3 className="text-xl font-bold text-[#2C1810] mb-1">{session.title}</h3>
                            <div className="text-sm text-[#6B4423] space-y-1">
                              <p>Mode: {session.mode === 'ai-generation' ? 'AI Generation' : session.mode === 'direct-text' ? 'Direct Text' : 'AI Paraphrase'}</p>
                              <p>Paragraphs: {session.paragraphs.length} | Tokens: {session.tokensUsed.toLocaleString()}</p>
                              <p>Saved: {new Date(session.savedAt).toLocaleString()}</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleLoadSession(session)}
                              className="retro-button-small bg-[#4D9E7C] hover:bg-[#3D8E6C]"
                            >
                              Load
                            </button>
                            <button
                              onClick={() => {
                                if (window.confirm(`Delete session "${session.title}"?`)) {
                                  handleDeleteSession(session.id);
                                }
                              }}
                              className="retro-button-small bg-[#E85D4D] hover:bg-[#D84D3D]"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
